import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import logo from "../assets/images/logo.png";

/* ================= GLOBAL ================= */

const GlobalStyle = createGlobalStyle`
  *{
    margin:0;
    padding:0;
    box-sizing:border-box;
  }

  html,body{
    width:100%;
    overflow-x:hidden;
    font-family:'Poppins',sans-serif;

    background:#050816;
  }
`;

/* ================= NAVBAR ================= */

const Navbar = styled.nav`
  width: 100%;
  height: 82px;

  position: sticky;
  top: 0;
  left: 0;

  z-index: 9999;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 42px;

  /* solid color instead of backdrop-filter blur — blur on a sticky
     element forces a GPU repaint on every scroll frame */
  background: #0b1226;

  border-bottom:
    1px solid rgba(255,255,255,0.06);

  box-shadow:
    0 10px 40px rgba(0,0,0,0.28);

  @media (max-width:768px){
    padding:0 18px;
    height:72px;
  }
`;

/* ================= LEFT ================= */

const NavLeft = styled.div`
  display:flex;
  align-items:center;
`;

const Brand = styled(Link)`
  display:flex;
  align-items:center;
  gap:12px;

  text-decoration:none;
`;

const LogoWrapper = styled.div`
  position: relative;

  &::before{
    content:'';

    position:absolute;

    inset:-6px;

    border-radius:20px;

    background:
      linear-gradient(
        135deg,
        rgba(95,46,234,0.4),
        rgba(10,132,255,0.4)
      );

    /* smaller blur radius — 18px blur on a hot element (sticky navbar,
       every frame) is expensive; 8px looks nearly identical */
    filter:blur(8px);

    z-index:-1;
  }
`;

const LogoImg = styled.img`
  height: 50px;
  width: 50px;

  object-fit: contain;

  border-radius: 16px;

  padding: 8px;

  background:
    linear-gradient(
      135deg,
      rgba(255,255,255,0.12),
      rgba(255,255,255,0.04)
    );

  border:
    1px solid rgba(255,255,255,0.08);

  box-shadow:
    0 0 30px rgba(10,132,255,0.22);

  transition: transform 0.35s ease;

  &:hover{
    transform:scale(1.06);
  }
`;

const BrandName = styled.span`
  font-family: "Poppins", sans-serif;

  font-size: 1.58rem;

  font-weight: 700;

  letter-spacing: 0.3px;

  /* static gradient instead of an infinite background-position
     animation — that animation repaints every frame, forever,
     on every single page */
  background:
    linear-gradient(
      135deg,
      #7c3aed 0%,
      #4f46e5 45%,
      #38bdf8 100%
    );

  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  text-shadow:
    0 0 18px rgba(79,70,229,0.28);

  @media(max-width:480px){
    font-size:1.32rem;
  }
`;

/* ================= CENTER ================= */

const NavCenter = styled.div`
  display:flex;
  align-items:center;

  gap:14px;

  flex:1;
  justify-content:center;

  font-family:'Poppins',sans-serif;

  @media(max-width:900px){
    display:none;
  }
`;

const StyledLink = styled(Link)`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  height: 44px;

  padding: 0 18px;

  border-radius: 14px;

  text-decoration: none;

  font-size: 0.97rem;
  font-weight: 600;

  letter-spacing: 0.3px;

  color: rgba(255,255,255,0.88);

  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  overflow: hidden;

  &:hover{
    color: #ffffff;
    background: rgba(255,255,255,0.08);
    box-shadow: 0 8px 24px rgba(10,132,255,0.18);
    transform: translateY(-1px);
  }

  &::after{
    content:'';

    position:absolute;

    left:18px;
    bottom:7px;

    width:0%;

    height:2px;

    border-radius:20px;

    background:
      linear-gradient(
        90deg,
        #5f2eea,
        #0a84ff
      );

    transition: width 0.2s ease;
  }

  &:hover::after{
    width:calc(100% - 36px);
  }
`;

/* ================= DROPDOWN ================= */

const Dropdown = styled.div`
  position:relative;
`;

const DropBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 46px;

  padding: 0 20px;

  border-radius: 15px;

  font-family: "Poppins", sans-serif;

  font-size: 0.96rem;

  font-weight: 600;

  letter-spacing: 0.35px;

  color: rgba(255,255,255,0.94);

  cursor: pointer;

  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  &:hover{
    color:#ffffff;
    background: rgba(255,255,255,0.08);
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(37,99,235,0.18);
  }
`;

const DropdownContent = styled.div`
  display:none;

  position:absolute;

  top:125%;
  left:0;

  min-width:260px;

  padding:10px;

  border-radius:20px;

  /* solid, slightly translucent background instead of
     backdrop-filter blur — this menu opens/closes constantly
     and blur recalculation is a real cost for something this common */
  background: rgba(10,18,40,0.98);

  border:
    1px solid rgba(255,255,255,0.06);

  box-shadow:
    0 20px 50px rgba(0,0,0,0.35);

  overflow:hidden;

  ${Dropdown}:hover &{
    display:block;
  }

  ${Dropdown}:focus-within &{
    display:block;
  }
`;

const DropdownLink = styled(Link)`
  display:block;

  padding:14px 16px;

  color:rgba(255,255,255,0.9);

  border-radius:14px;

  text-decoration:none;

  font-weight:500;

  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover{
    background: rgba(255,255,255,0.06);
    transform:translateX(4px);
    color:#8ec5ff;
  }
`;

/* ================= RIGHT ================= */

const NavRight = styled.div`
  display:flex;
  align-items:center;

  @media(max-width:900px){
    display:none;
  }
`;

const AuthBtn = styled(Link)`
  padding:12px 22px;

  border-radius:14px;

  background:
    linear-gradient(
      135deg,
      #5f2eea,
      #0a84ff
    );

  color:white;

  font-weight:700;

  text-decoration:none;

  border:none;

  cursor:pointer;

  box-shadow:
    0 10px 25px rgba(10,132,255,0.25);

  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover{
    transform:translateY(-2px);
    box-shadow: 0 14px 35px rgba(10,132,255,0.35);
  }
`;

/* ================= MOBILE ================= */

const Hamburger = styled.div`
  display:none;

  font-size:30px;

  color:white;

  cursor:pointer;

  transition:transform 0.2s ease;

  &:hover{
    transform:scale(1.08);
  }

  @media(max-width:900px){
    display:block;
  }
`;

/* CSS-only animation instead of framer-motion — same fade/slide-down
   effect without pulling in an extra ~40kb+ animation library */
const MobileMenu = styled.div`
  width:100%;

  display:flex;
  flex-direction:column;

  gap:12px;

  padding:20px;

  background: rgba(8,15,35,0.98);

  border-top:
    1px solid rgba(255,255,255,0.06);

  animation: mobileMenuIn 0.22s ease;

  @keyframes mobileMenuIn {
    from {
      opacity: 0;
      transform: translateY(-12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MobileLink = styled(Link)`
  color:white;

  text-decoration:none;

  font-weight:600;

  padding:14px 16px;

  border-radius:14px;

  transition: background 0.2s ease, color 0.2s ease;

  &:hover{
    background: rgba(255,255,255,0.06);
    color:#8ec5ff;
  }
`;

/* ================= COMPONENT ================= */

export default function UserNavbar({
  isAuthenticated,
  handleLogout
}) {

  const [mobileOpen, setMobileOpen] =
    useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <GlobalStyle />

      <Navbar>

        {/* LEFT */}

        <NavLeft>

          <Brand to="/">

            <LogoWrapper>
              <LogoImg
                src={logo}
                alt="Berrybloomer Logo"
                width={50}
                height={50}
                loading="eager"
                fetchpriority="high"
              />
            </LogoWrapper>

            <BrandName>
              Berrybloomer
            </BrandName>

          </Brand>

        </NavLeft>

        {/* CENTER */}

        <NavCenter>

          <StyledLink to="/">
            Home
          </StyledLink>

          <Dropdown tabIndex={0}>

            <DropBtn>
              Services ▾
            </DropBtn>

            <DropdownContent>

              <DropdownLink to="/android">
                AI Android App
              </DropdownLink>

              <DropdownLink to="/ios">
                AI IOS App
              </DropdownLink>

              <DropdownLink to="/webapp">
                AI Web App
              </DropdownLink>

              <DropdownLink to="/uiux">
                AI UI/UX Design
              </DropdownLink>

              <DropdownLink to="/GraphicDesign">
                Graphic Designing
              </DropdownLink>

              <DropdownLink to="/SocialMediaMarketing">
                Social Media Marketing
              </DropdownLink>

            </DropdownContent>

          </Dropdown>

          <Dropdown tabIndex={0}>

            <DropBtn>
              Portfolio ▾
            </DropBtn>

            <DropdownContent>

              <DropdownLink to="/pages/projects">
                Our Projects
              </DropdownLink>

              <DropdownLink to="/pages/testimonials">
                Testimonials
              </DropdownLink>

            </DropdownContent>

          </Dropdown>

          <StyledLink to="/pages/career">
            Careers
          </StyledLink>

          <StyledLink to="/pages/contact">
            Contact
          </StyledLink>

        </NavCenter>

        {/* RIGHT */}

        <NavRight>

          {!isAuthenticated ? (

            <AuthBtn to="/login">
              Login
            </AuthBtn>

          ) : (

            <AuthBtn
              as="button"
              onClick={handleLogout}
            >
              Logout
            </AuthBtn>

          )}

        </NavRight>

        {/* MOBILE */}

        <Hamburger
          onClick={() =>
            setMobileOpen(!mobileOpen)
          }
        >
          {mobileOpen ? "✕" : "☰"}
        </Hamburger>

      </Navbar>

      {/* MOBILE MENU */}

      {mobileOpen && (

        <MobileMenu>

          <MobileLink to="/" onClick={closeMobile}>
            Home
          </MobileLink>

          <MobileLink to="/android" onClick={closeMobile}>
            AI Android App
          </MobileLink>

          <MobileLink to="/ios" onClick={closeMobile}>
            AI IOS App
          </MobileLink>

          <MobileLink to="/webapp" onClick={closeMobile}>
            AI Web App
          </MobileLink>

          <MobileLink to="/uiux" onClick={closeMobile}>
            AI UI/UX
          </MobileLink>

          <MobileLink to="/GraphicDesign" onClick={closeMobile}>
            Graphic Designing
          </MobileLink>

          <MobileLink to="/SocialMediaMarketing" onClick={closeMobile}>
            Social Media Marketing
          </MobileLink>

          <MobileLink to="/pages/projects" onClick={closeMobile}>
            Projects
          </MobileLink>

          <MobileLink to="/pages/testimonials" onClick={closeMobile}>
            Testimonials
          </MobileLink>

          <MobileLink to="/pages/career" onClick={closeMobile}>
            Careers
          </MobileLink>

          <MobileLink to="/pages/contact" onClick={closeMobile}>
            Contact
          </MobileLink>

          {!isAuthenticated ? (

            <MobileLink to="/login" onClick={closeMobile}>
              Login
            </MobileLink>

          ) : (

            <MobileLink
              as="button"
              onClick={() => {
                handleLogout();
                closeMobile();
              }}
            >
              Logout
            </MobileLink>

          )}

        </MobileMenu>

      )}

    </>
  );
}